/**
 * Shared Needs Action data fetching.
 *
 * Both the Needs Action page and the sidebar badge need the same query: the
 * Needs Action views assigned to the current user's role, OR'd together, and
 * the orders (and total count) that match any of them. Centralizing it keeps
 * the page and the nav badge in sync.
 *
 * Which views exist is not a lender-admin free-for-all: Reggora publishes a
 * catalog of templates and the admin assigns them to roles, so this endpoint
 * returns only what the caller's own role has been given. An empty result is
 * meaningful -- it means the page and nav item should not be shown at all.
 */

/**
 * Fetch the Needs Action views assigned to the current user's role.
 * @param {ReturnType<import('@app/apiClient').default>} api
 * @returns {Promise<Array<{id: string, name: string}>>}
 */
export const fetchNeedsActionViews = async (api) => {
  const viewsResponse = await api.get('lender/needs_action/views/')
  return viewsResponse.results || []
}

/**
 * How many cards a page of the queue holds.
 *
 * The orders endpoint defaults `limit` to 10, so an explicit value is required:
 * without one the page silently showed 10 of N while the badge reported the
 * true total.
 */
export const PAGE_SIZE = 25

/**
 * Fetch the needs-action views plus the orders/count matching ANY of them.
 *
 * Filtering happens on the server, not over the fetched page. Narrowing to one
 * action type means sending fewer ids in `pipeline_views`, and narrowing to a
 * person means sending `assigned` -- so the count, the pagination and the
 * matched-view tagging all stay correct. Filtering client-side would only ever
 * filter the current page and make "Showing X of Y" lie.
 *
 * @param {ReturnType<import('@app/apiClient').default>} api
 * @param {{
 *   limit?: number,
 *   offset?: number,
 *   viewIds?: Array<string>,
 *   assigned?: Array<string>,
 * }} [options] limit caps the returned orders (the count is always the true
 *   total); pass a small limit when only the count is needed (e.g. the nav
 *   badge). offset pages through them. viewIds narrows to a subset of the
 *   caller's views; assigned narrows to orders assigned to those users.
 * @returns {Promise<{ views: Array, orders: Array, count: number }>}
 */
export const fetchNeedsActionData = async (
  api,
  { limit = PAGE_SIZE, offset = 0, viewIds, assigned } = {},
) => {
  const views = await fetchNeedsActionViews(api)
  if (views.length === 0) {
    return { views: [], orders: [], count: 0 }
  }

  // An empty or absent selection means "all of mine", not "none": a filter the
  // user has not touched must not silently empty the queue. A selection naming
  // only unknown ids falls back the same way rather than querying for nothing.
  const selected = (viewIds || []).filter((id) => views.some((view) => view.id === id))
  const pipelineViewIds = selected.length > 0 ? selected : views.map((v) => v.id)

  const params = {
    pipeline_views: pipelineViewIds,
    // Multiple needs-action views are OR'd: an order shows if it matches ANY
    // assigned view, not all of them.
    pipeline_views_combinator: 'or',
    // Tag each order with the view(s) it matched (order.matched_pipeline_views).
    include_matched_views: true,
    offset,
    // Longest-waiting first: when the order's oldest open needs-action item was
    // raised, derived at index time across every needs-action type. Orders not
    // yet reindexed with it sort last rather than failing -- the backend sorts
    // it with unmapped_type/missing, so that depends on the backend's guard.
    ordering: 'needs_action_since',
    limit,
  }

  // Comma-joined, because that is what the orders endpoint splits on. Omitted
  // entirely when empty so the queue is not narrowed to nobody.
  if (assigned && assigned.length > 0) {
    params.assigned = assigned.join(',')
  }

  const ordersResponse = await api.get('order/full/', params)
  const orders = ordersResponse.results || ordersResponse.orders || []
  const count = typeof ordersResponse.count === 'number' ? ordersResponse.count : orders.length

  return { views, orders, count }
}

/**
 * Options for the toolbar: action types, and — for a holder of the admin scope
 * — the assignees, both with counts.
 *
 * Separate from the queue fetch on purpose. The queue is paginated and changes
 * as the reviewer works; the toolbar's options are not, and refetching a roster
 * every time someone presses Load more would be wasted work.
 *
 * @param {ReturnType<import('@app/apiClient').default>} api
 * @param {{ viewIds?: Array<string>, assigned?: Array<string> }} [options]
 *   the filters already applied, so the counts narrow with them. The lists
 *   themselves always come back whole.
 * @returns {Promise<{ actionTypes: Array, users: Array }>}
 */
export const fetchNeedsActionFilters = async (api, { viewIds, assigned } = {}) => {
  const params = {}
  if (viewIds && viewIds.length > 0) {
    params.pipeline_views = viewIds
    params.pipeline_views_combinator = 'or'
  }
  if (assigned && assigned.length > 0) {
    params.assigned = assigned.join(',')
  }

  const response = await api.get('lender/needs_action/filters/', params)
  return {
    actionTypes: response.action_types || [],
    // Empty for anyone without the admin scope -- the endpoint decides that,
    // not the caller, so the frontend cannot ask for a roster it should not see.
    users: response.users || [],
    // Null for anyone without the admin scope, and left null rather than zeroed
    // so the segment can tell "no counts available" from "nothing in the queue".
    scopeCounts: response.scope_counts || null,
  }
}
